import { useEffect, useRef } from "react";

import { msalInstance } from "@/lib/auth";

/**
 * Blank landing route for the MSAL auth response (`/auth-redirect`).
 *
 * Popup flow: the opener detects the response on this URL and closes the popup,
 * so this page does nothing. Redirect flow (no opener): process the hash, then
 * return to the app root. Kept out of the app shell so no chat/embed UI boots
 * here.
 */
export default function AuthRedirect() {
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    // Framed: this is MSAL's hidden silent-renewal iframe, and the parent is
    // polling this exact URL for the response fragment. Touch nothing. The
    // opener check below does NOT cover it -- a hidden iframe has no opener --
    // so without this guard the `location.replace("/")` at the end of this
    // effect navigates away from the very fragment the parent is waiting for
    // (and boots a second copy of the app inside the frame). The parent reads
    // the hash every 30ms and usually wins the race, which is exactly what
    // makes this the kind of bug that shows up as an occasional sign-in
    // failure rather than a reproducible one.
    if (window.parent !== window) return;

    // Popup: leave the response for the opener to read; don't self-close (that
    // could race the opener reading the popup URL).
    if (window.opener && window.opener !== window) return;

    (async () => {
      try {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
      } catch (e) {
        console.error("Auth redirect handling failed", e);
      }
      window.location.replace("/");
    })();
  }, []);

  return (
    <div className="grid h-screen w-full place-items-center text-sm text-muted-foreground">
      Aanmelden…
    </div>
  );
}
